// ---------------------------------------------------------------------------
// Per-route document head (SEO / social).
//
// index.html ships ONE static <title> + description + OG/Twitter set that every
// SPA route inherits. That is fine for the app shell but weak for search and
// share previews: every public page looked identical to Google, Bing, AI
// crawlers, and link unfurlers. This module gives each public, prerendered page
// its own title, meta description, canonical URL, and social tags.
//
// It runs on the client on every route change (so client-side navigation
// updates the tab title too), and — crucially — it runs before the prerender
// readiness marker fires (see src/prerender.ts and App.tsx wiring order), so the
// headless snapshot a crawler receives carries the correct, page-specific head.
//
// Copy discipline: every line is factual and verifiable, no fabricated claims,
// no hype words, "verdict" and "prior art" kept as owned terms. Titles ≤ ~60
// chars, descriptions ≤ ~160 chars to fit search snippet limits.
// ---------------------------------------------------------------------------

import { useEffect } from 'react';
import { SITE_ORIGIN } from './utils/siteOrigin';
import { buildStructuredData } from './structuredData';
import { getCachedProductRating, hasPublishableRating } from './productRating';
import { PUBLIC_PAGES, PUBLIC_PAGES_BY_PATH } from '../../../methods/src/common/publicPages';

interface PageSeo {
  title: string;
  description: string;
}

/**
 * Brand default — mirrors the static tags in index.html. Applied to any route
 * NOT in the map below (the whole authenticated app, /try, /spark, the A/B
 * variants), so navigating away from a mapped page never leaves a stale title.
 */
const DEFAULT_SEO: PageSeo = {
  title: 'EntreDash — Spark to business',
  // Kept under ~160 chars so it isn't truncated in search snippets (the old
  // copy ran 272). This is the fallback for routes not in ROUTE_SEO and for the
  // static shell served to non-JS crawlers, so it has to stand on its own.
  description:
    'From a random idea to a real, buildable, patentable invention. EntreDash gives everyday inventors free strategy sessions and real prior-art research.',
};

/**
 * Per-route SEO, keyed by exact pathname — DERIVED from the public-page registry
 * (methods/src/common/publicPages.ts), the single source of truth every public
 * page runs through. These are exactly the routes opted into prerendering (the
 * beacon set and web.json both derive from / mirror the same registry), so a
 * crawler captures the right head with the right body, and the title/description
 * here can never drift from the sitemap or the breadcrumb labels again.
 */
export const ROUTE_SEO: Record<string, PageSeo> = Object.fromEntries(
  PUBLIC_PAGES.map((p) => [p.path, { title: p.title, description: p.description }]),
);

function setMetaByName(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaByProperty(property: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Set (or, with null, remove) the single canonical link. */
function setCanonical(href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Replace the route's structured-data (JSON-LD) block. One tagged <script> holds
 * the whole @graph; on each route change we remove the previous one and write the
 * fresh graph, so SPA navigation never accumulates or leaves a stale graph. It's
 * written into <head> synchronously inside applyDocumentHead, before the
 * prerender readiness marker fires, so a crawler's snapshot includes it.
 */
// Remember the last route we wrote structured data for, so refreshStructuredData()
// can re-emit the same route's graph once an async input (the product rating)
// arrives without needing the caller to re-supply the pathname.
let lastStructuredDataPath: string | null = null;

function setStructuredData(pathname: string): void {
  lastStructuredDataPath = pathname;
  const prev = document.head.querySelector('script[data-seo-jsonld]');
  if (prev) prev.remove();
  const el = document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute('data-seo-jsonld', '');
  // Emit an aggregateRating only when there's a genuine, large-enough sample of
  // in-app memo ratings (see productRating.ts). Until then this is null and the
  // SoftwareApplication node carries no rating — never a fabricated one.
  const cachedRating = getCachedProductRating();
  const rating = hasPublishableRating(cachedRating) ? cachedRating : null;
  // No raw-HTML injection risk: this is a text node inside a script element, not
  // parsed as markup. The blog renderer escapes `<` for defense in depth; here
  // the content is entirely first-party constants and the page's own FAQ copy.
  el.textContent = JSON.stringify(buildStructuredData(pathname, rating));
  document.head.appendChild(el);
}

/**
 * Re-emit the current route's structured data. Called after the product rating
 * loads asynchronously so the homepage's JSON-LD can pick up a freshly-available
 * aggregateRating. No-op until a route has been rendered.
 */
export function refreshStructuredData(): void {
  if (lastStructuredDataPath !== null) setStructuredData(lastStructuredDataPath);
}

/** Set (or, with null, remove) the og:url property meta. */
function setOgUrl(url: string | null): void {
  const el = document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]');
  if (url === null) {
    if (el) el.remove();
    return;
  }
  setMetaByProperty('og:url', url);
}

/**
 * Reachable-by-anonymous routes that must NEVER be indexed. These are:
 *   - the interactive assessment runtime that gates on a privacy confirmation
 *     and redirects a cold visitor to "/" (/try) — no standalone search value;
 *   - functional auth / app screens with no content worth ranking (/signup, and
 *     /strategy, which returns a 200 SPA shell to a logged-out crawler and then
 *     gates to sign-in rather than being a true 404/redirect, so it must assert
 *     noindex explicitly rather than rely on being "unreachable");
 *   - the unlisted A/B landing variants (duplicate content that would compete
 *     with the real "/").
 * None are pre-rendered or in the sitemap, but Google can still discover them
 * via internal links, ad links, or its own probing — so we assert `noindex`
 * explicitly. Every other route is indexable by default (governed by ROUTE_SEO
 * + the blog's own static pages).
 *
 * NOTE: /spark is deliberately NOT here. Its anonymous front door (SparkPage)
 * is a real landing page with a synchronous H1/hero, so it is prerendered, has
 * its own ROUTE_SEO entry + canonical, and is listed in the sitemap — it is
 * meant to rank.
 */
const NOINDEX_ROUTES = new Set<string>([
  '/try',
  '/signup',
  '/strategy',
  '/va', '/vb', '/vc', '/vd', '/ve', '/vf', '/vg', '/v2',
]);

/**
 * The authenticated APP surface — member dashboards, idea workspaces, settings,
 * and the in-app tools. A logged-out crawler is served a 200 SPA shell for these
 * (they gate client-side rather than 404), so they'd otherwise look like a wall
 * of indexable, canonical-less pages sharing one default title. We assert
 * `noindex` on them so search engines that render JS drop them; `robots.txt`
 * disallows the same prefixes so crawlers that DON'T render JS never fetch them.
 *
 * These are PREFIXES, matched on a full path segment (`/ideas` covers
 * `/ideas/:id/...` but never a hypothetical `/ideasomething`). Kept in lockstep
 * with the `robots.txt` disallow list. Deliberately does NOT include the public
 * marketing pages, the blog, `/learn/*`, `/modules/*`, or `/advisors/*` (which
 * may carry public value), nor the ad-destination variants / `/try` (already
 * governed by NOINDEX_ROUTES, and left crawlable so Google Ads can reach them).
 */
const PRIVATE_APP_PREFIXES = [
  // Shared memo links: a confidential memo behind a secret URL. Never indexed.
  '/shared',
  '/dashboard',
  '/ideas',
  '/settings',
  '/intelligence',
  '/signals',
  '/search-strategy',
  '/strategy',
  '/business-planning',
  '/invention-log',
  // Invention Log pages (private records and print views).
  '/log',
  // Public record check: works by code, never indexed.
  '/check',
  // Public witness pages: private links, never indexed.
  '/witness',
  '/badges',
  '/leaderboard',
  '/timesheet',
  '/bd',
  '/admin',
  // The member-only Spark history. Distinct from the public `/spark` front
  // door, which stays indexable — the segment match below never catches it.
  '/spark/library',
  // The drawings quick start (guest drafts): functional, per-visitor, never indexed.
  '/invention-concept-drawings/start',
];

/** Whether a path is part of the private authenticated app surface. */
function isPrivateAppPath(pathname: string): boolean {
  return PRIVATE_APP_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Set or clear the robots meta. Absent = indexable (the default). */
function setRobots(noindex: boolean): void {
  let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!noindex) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  // `follow` so the crawler still traverses links out of the page even though
  // the page itself stays out of the index.
  // A shared memo link is a confidential document: no index, no following its source links, no cache.
  el.setAttribute('content', /^\/(shared|witness)\//.test(location.pathname) ? 'noindex, nofollow, noarchive' : 'noindex, follow');
}

// Brand default share image — captured ONCE at module load from the og:image
// index.html ships (a designed 1200x630 card), before any route change can
// mutate it. Used to restore the brand image when navigating to a page that
// declares no ogImage of its own, so a per-page override never leaks across a
// client-side navigation. Falls back to the app icon only if the tag is absent.
const DEFAULT_OG_IMAGE =
  (typeof document !== 'undefined' &&
    document.head.querySelector('meta[property="og:image"]')?.getAttribute('content')) ||
  `${SITE_ORIGIN}/icon-512.png`;

/** Set og:image + twitter:image together (both point at the same asset). */
function setShareImage(url: string): void {
  setMetaByProperty('og:image', url);
  setMetaByName('twitter:image', url);
}

/**
 * Apply the head for a pathname. Mapped routes get their own title, description,
 * OG/Twitter text, and a canonical URL; every other route falls back to the
 * brand default and drops the canonical (an app screen has no canonical URL).
 * A registered page may declare its own `ogImage`; absent one (every page today)
 * the OG/Twitter image stays the brand share image index.html set.
 */
export function applyDocumentHead(pathname: string): void {
  if (typeof document === 'undefined') return;
  const mapped = ROUTE_SEO[pathname];
  // Shared memo links get a generic head: never the idea title or verdict, even in a link preview.
  const seo = pathname.startsWith('/shared/')
    ? { title: 'Confidential memo · EntreDash', description: 'A confidential memo was shared with you.' }
    : pathname.startsWith('/witness/')
      ? { title: 'Witness entries · EntreDash', description: 'You were asked to witness entries in an invention log.' }
      : pathname.startsWith('/merge-accounts/')
        ? { title: 'Combine your accounts · EntreDash', description: 'Combine two EntreDash accounts into one.' }
        : mapped ?? DEFAULT_SEO;

  document.title = seo.title;
  setMetaByName('description', seo.description);
  setMetaByProperty('og:title', seo.title);
  setMetaByProperty('og:description', seo.description);
  setMetaByName('twitter:title', seo.title);
  setMetaByName('twitter:description', seo.description);

  // Per-page share image from the registry, if one is declared; otherwise the
  // brand default. A relative path is resolved against the canonical origin.
  const ogImage = PUBLIC_PAGES_BY_PATH[pathname]?.ogImage;
  setShareImage(
    ogImage ? (ogImage.startsWith('http') ? ogImage : `${SITE_ORIGIN}${ogImage}`) : DEFAULT_OG_IMAGE,
  );

  // Canonical URL. Mapped public pages are self-referencing. `/landing` is a
  // deliberate DUPLICATE of the home page (a signed-in "revisit the marketing
  // site" alias that renders the same LandingPage), so it declares the home
  // page as its canonical — consolidating it onto "/" instead of competing with
  // it as duplicate content with a duplicate title. Every other (app) route
  // carries no canonical (it isn't an indexable content URL).
  const canonical =
    pathname === '/landing'
      ? `${SITE_ORIGIN}/`
      : mapped
        ? `${SITE_ORIGIN}${pathname}`
        : null;
  setCanonical(canonical);
  setOgUrl(canonical);

  // Assert noindex on the product front doors + A/B variants AND the whole
  // authenticated app surface (dashboards, idea workspaces, settings, in-app
  // tools) — a logged-out crawler is served a 200 shell for those, so without
  // this they look like indexable, canonical-less pages sharing one default
  // title. Everything else (public marketing pages, the blog, /learn/*) indexes
  // by default. /try in particular only functions after a privacy confirmation,
  // so it should never appear in search results.
  setRobots(NOINDEX_ROUTES.has(pathname) || isPrivateAppPath(pathname));

  // Structured data: the global Organization + WebSite identity on every route,
  // plus route-specific nodes (the app + FAQ on home, a breadcrumb on mapped
  // sub-pages). See src/structuredData.ts.
  setStructuredData(pathname);
}

/** Apply the per-route head whenever the pathname changes. */
export function useDocumentHead(pathname: string): void {
  useEffect(() => {
    applyDocumentHead(pathname);
  }, [pathname]);
}
